import React, { useEffect, useState } from "react";
import ExportPDF from "../../components/const/ExportPDF";
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    PointerSensor,
    TouchSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    rectSortingStrategy,
} from "@dnd-kit/sortable";

import {
    getPhotoBoardDetail,
    updatePhotoBoard,
    deletePhotoBoard,
} from "../../api/const";
import { useParams, useNavigate } from "react-router-dom";
import PhotoCell from "../../components/const/Photo/PhotoCell";
import PhotoBoardHeader from "../../components/const/Photo/PhotoBoardHeader";
import PageHeader from "../../components/common/ui/PageHeader";

export default function PhotoBoardEditPage() {
    const { photoboardId } = useParams();
    const navigate = useNavigate();

    const [board, setBoard] = useState(null);
    const [gridX, setGridX] = useState(1);
    const [gridY, setGridY] = useState(1);
    const [cells, setCells] = useState([]);
    const [editing, setEditing] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
    );

    // 가로/세로 비율 결정
    const isLandscape = gridY <= gridX;
    const canvasWidth = isLandscape ? 1500 : 700;
    const canvasHeight = isLandscape ? 650 : 1000;
    const pdfOrientation = isLandscape ? "landscape" : "portrait";

    // 📌 데이터 로딩
    useEffect(() => {
        getPhotoBoardDetail(photoboardId).then((data) => {
            setBoard(data);

            const x = data?.grid_x || 1;
            const y = data?.grid_y || 1;

            setGridX(x);
            setGridY(y);

            const total = x * y;
            const initCells = Array.from({ length: total }, (_, i) => ({
                id: `셀-${i + 1}`,
                photo: data.photos?.[i] || null,
            }));
            setCells(initCells);
        });
    }, [photoboardId]);

    // 🧩 그리드 적용
    const handleGridApply = () => {
        const total = gridX * gridY;
        setCells((prev) =>
            Array.from({ length: total }, (_, i) => ({
                id: `셀-${i + 1}`,
                photo: prev[i]?.photo || null,
            }))
        );
    };

    // 🔄 드래그 종료
    const handleDragEnd = (event) => {
        if (!editing) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = cells.findIndex((c) => c.id === active.id);
        const newIndex = cells.findIndex((c) => c.id === over.id);

        setCells(arrayMove(cells, oldIndex, newIndex));
    };

    // 🖼 셀 수정/삭제
    const handleSelectPhoto = (cellId, photo) => {
        setCells((prev) =>
            prev.map((c) => (c.id === cellId ? { ...c, photo } : c))
        );
    };

    const handleDeleteCell = (cellId) => {
        setCells((prev) =>
            prev.map((c) => (c.id === cellId ? { ...c, photo: null } : c))
        );
    };

    // 💾 저장
    const handleSave = async () => {
        try {
            await updatePhotoBoard(photoboardId, {
                title: board?.title,
                confirmer: board?.confirmer,
                confirmer_id: board?.confirmer_id,
                date: board?.date,
                grid_x: gridX,
                grid_y: gridY,
                photo_ids: cells.map((c) => c.photo?.id).filter(Boolean),
            });
            alert("저장되었습니다!");
            setEditing(false);
        } catch {
            alert("저장 실패!");
        }
    };

    // 🗑 삭제
    const handleDelete = async () => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return;

        try {
            await deletePhotoBoard(photoboardId);
            alert("삭제되었습니다!");
            navigate(-1);
        } catch {
            alert("삭제 실패!");
        }
    };

    if (!board) return <div className="p-6">로딩 중...</div>;

    return (
        <div className="p-6">

            {/* 📌 페이지 헤더 */}
            <PageHeader>사진대지</PageHeader>

            {/* 상단 버튼 영역 */}
            <div className="flex justify-between items-center">
                <div />

                <div className="flex gap-2 mb-4">
                    {editing ? (
                        <>
                            <button onClick={handleSave} className="btn-save">
                                저장
                            </button>
                            <button onClick={() => setEditing(false)} className="btn-basic">
                                취소
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setEditing(true)} className="btn-basic">
                            수정
                        </button>
                    )}

                    <ExportPDF
                        contentId="photoboard-view-canvas"
                        orientation={pdfOrientation}
                    />

                    <button onClick={handleDelete} className="btn-delete">
                        삭제
                    </button>

                    <button onClick={() => navigate(-1)} className="btn-basic">
                        뒤로가기
                    </button>
                </div>
            </div>

            {/* 📌 기본 정보 카드 */}
            <div className="page-box">
                <PhotoBoardHeader
                    board={board}
                    editing={editing}
                    onChange={(field, value) =>
                        setBoard((prev) => ({ ...prev, [field]: value }))
                    }
                />
                {/* 📌 그리드 설정 */}
                {editing && (
                    <div className="bg-white border rounded-lg p-4 mt-6 mb-6">

                        {/* 제목 */}
                        <div className="text-sm font-semibold text-gray-700 mb-3">
                            그리드 설정
                        </div>

                        {/* 입력 영역 */}
                        <div className="flex items-center gap-3">
                            <label className="text-sm text-gray-600">가로</label>
                            <input
                                type="number"
                                value={gridX}
                                min={1}
                                onChange={(e) => setGridX(Number(e.target.value))}
                                className="input-base w-20"
                            />

                            <label className="text-sm text-gray-600">세로</label>
                            <input
                                type="number"
                                value={gridY}
                                min={1}
                                onChange={(e) => setGridY(Number(e.target.value))}
                                className="input-base w-20"
                            />

                            <button onClick={handleGridApply} className="btn-basic-sm">
                                적용
                            </button>
                        </div>
                    </div>
                )}


                {/* 📌 사진대지 캔버스 */}
                <div className="flex justify-center mt-6">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={cells.map((c) => c.id)} strategy={rectSortingStrategy}>
                            <div
                                id="photoboard-view-canvas"
                                style={{
                                    width: `${canvasWidth}px`,
                                    height: `${canvasHeight}px`,
                                    display: "grid",
                                    gridTemplateColumns: `repeat(${gridX}, 1fr)`,
                                    gridTemplateRows: `repeat(${gridY}, 1fr)`,
                                    gap: "4px",
                                }}
                            >
                                {cells.map((cell) => (
                                    <PhotoCell
                                        key={cell.id}
                                        id={cell.id}
                                        photo={cell.photo}
                                        projectId={board.project}
                                        onSelect={handleSelectPhoto}
                                        onDelete={handleDeleteCell}
                                        disabled={!editing}
                                    />
                                ))}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
        </div>
    );
}
